// src/App.jsx  —  ohapotato v2026.10.36
import React, { useCallback, useState, useEffect, useRef } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import './styles/theme.css';

import { useTheme }        from './hooks/useTheme';
import { useIngredients }  from './hooks/useIngredients';
import { INGREDIENTS, STAPLES } from './data/ingredients';
import { useRecipes }      from './hooks/useRecipes';
import { useSavedRecipes } from './hooks/useSavedRecipes';
import { useProfile }      from './hooks/useProfile';
import { useRateLimit }    from './hooks/useRateLimit';

import Header           from './components/Header';
import HeroSection      from './components/HeroSection';
import IngredientPicker from './components/IngredientPicker';
import ResultsPanel     from './components/ResultsPanel';
import MobileFindBar    from './components/MobileFindBar';
import ProfilePanel     from './components/ProfilePanel';
import MealPlanner      from './components/MealPlanner';
import KitchenPanel     from './components/KitchenPanel';
import { useMyPantry }   from './hooks/useMyPantry';
import { useGroceryList } from './hooks/useGroceryList';

import AboutPage   from './pages/AboutPage';
import TipsIndex  from './pages/tips/TipsIndex';
import UseUpVegetables      from './pages/tips/UseUpVegetables';
import PantryStaplesGuide   from './pages/tips/PantryStaplesGuide';
import BatchCookingGuide    from './pages/tips/BatchCookingGuide';
import IngredientSubstitutions from './pages/tips/IngredientSubstitutions';
import ReduceFoodWaste      from './pages/tips/ReduceFoodWaste';
import KnifeSkillsGuide     from './pages/tips/KnifeSkillsGuide';
import SeasoningGuide       from './pages/tips/SeasoningGuide';
import ProteinCookingGuide  from './pages/tips/ProteinCookingGuide';
import MealPlanningGuide    from './pages/tips/MealPlanningGuide';
import BudgetCookingGuide   from './pages/tips/BudgetCookingGuide';
import ContactPage from './pages/ContactPage';
import PrivacyPage    from './pages/PrivacyPage';
import TermsPage      from './pages/TermsPage';
import DisclaimerPage from './pages/DisclaimerPage';

export default function App() {
  const { theme, toggleTheme }    = useTheme();
  const { user, isAuthenticated } = useAuth0();

  const userId = isAuthenticated && user ? user.sub : null;
  const { profile, updateProfile }                    = useProfile(userId);
  const { saved, savedIds, saveRecipe, removeRecipe } = useSavedRecipes(userId);

  // Kitchen state — available to all users regardless of auth/premium
  const { pantry, togglePantry, clearPantry } = useMyPantry();
  const { items: groceryItems, addItem: addGroceryItem, addItems: addGroceryItems,
          toggleItem: toggleGrocery, removeItem: removeGrocery,
          clearDone: clearGroceryDone, clearAll: clearGroceryAll } = useGroceryList();
  const { consume, blocked, clearBlock, remaining } = useRateLimit({ isAuthenticated });

  const {
    selected, toggle, remove, clear,
    category, setCategory,
    search, setSearch,
    filtered, selectedList, selectMultiple,
    nonStapleCount,
  } = useIngredients();

  const { recipes, loading, error, hasSearched, search: searchRecipes, reset } = useRecipes({ consume });
  const totalXP = recipes.reduce((sum, r) => sum + Math.round(10 + ((r.matchPercent||0)/100)*90), 0);

  // Sync My Pantry → Step 1 selection: whenever pantry changes, ensure those
  // ingredient IDs are included in the selected set (without removing others).
  useEffect(() => {
    if (pantry.size > 0) {
      const labels = INGREDIENTS
        .filter(i => pantry.has(i.id))
        .map(i => i.label);
      selectMultiple(labels);
    }
  }, [pantry]); // eslint-disable-line react-hooks/exhaustive-deps

  // Panel state
  const [profileTab, setProfileTab]         = useState(null);  // null | 'saved' | 'profile' | 'premium'
  const [mealPlannerOpen, setMealPlannerOpen]   = useState(false);


  const resultsRef = useRef(null);

  const handleHome = useCallback(() => {
    clear();          // deselect all ingredients
    reset();          // clear recipe results
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [clear, reset]);

  const handleSuggest = useCallback((items, mealType) => {
    selectMultiple(items);
    setTimeout(() => {
      const currentStapleLabels = STAPLES.filter(s => s.staple).map(s => s.label);
      searchRecipes(items, currentStapleLabels, mealType);
      setTimeout(() => resultsRef.current?.scrollIntoView({ behavior:'smooth', block:'start' }), 200);
    }, 300);
  }, [selectMultiple, searchRecipes]);

  const handleFind = useCallback(() => {
    const allLabels = selectedList.map(i => i.label);
    const stapleLabels = selectedList.filter(i => i.staple).map(i => i.label);
    searchRecipes(allLabels, stapleLabels);
  }, [selectedList, searchRecipes]);

  const handleOpenMealPlanner = useCallback(() => {
    setMealPlannerOpen(true);
  }, []);



  // Show welcome toast when Stripe redirects back after successful checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('checkout') === 'success') {
      // Clean the query param from the URL without a page reload
      window.history.replaceState({}, '', window.location.pathname);
      // Auto-dismiss after 6 seconds
    }
  }, []);

  return (
    <Routes>
      <Route path="/about"   element={<AboutPage />} />
      <Route path="/tips"    element={<TipsIndex />} />
      <Route path="/tips/use-up-vegetables"      element={<UseUpVegetables />} />
      <Route path="/tips/pantry-staples-guide"   element={<PantryStaplesGuide />} />
      <Route path="/tips/batch-cooking-guide"    element={<BatchCookingGuide />} />
      <Route path="/tips/ingredient-substitutions" element={<IngredientSubstitutions />} />
      <Route path="/tips/reduce-food-waste"      element={<ReduceFoodWaste />} />
      <Route path="/tips/knife-skills-guide"     element={<KnifeSkillsGuide />} />
      <Route path="/tips/seasoning-guide"        element={<SeasoningGuide />} />
      <Route path="/tips/protein-cooking-guide"  element={<ProteinCookingGuide />} />
      <Route path="/tips/meal-planning-guide"    element={<MealPlanningGuide />} />
      <Route path="/tips/budget-cooking-guide"   element={<BudgetCookingGuide />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/privacy"    element={<PrivacyPage />} />
      <Route path="/terms"      element={<TermsPage />} />
      <Route path="/disclaimer" element={<DisclaimerPage />} />
      <Route path="/*" element={
        <div style={{ minHeight:'100vh', display:'flex', flexDirection:'column' }}>
          <Header
            theme={theme}
            onToggleTheme={toggleTheme}
            onViewProfile={(tab) => setProfileTab(tab || 'saved')}
            
            onOpenMealPlanner={handleOpenMealPlanner}
            onHome={handleHome}
            totalXP={totalXP}
          />

          <main className="app-main">
            <HeroSection />

            <div style={st.toolbar}>
              <KitchenPanel
                pantry={pantry}
                onTogglePantry={togglePantry}
                onClearPantry={clearPantry}
                groceryItems={groceryItems}
                onToggleGrocery={toggleGrocery}
                onRemoveGrocery={removeGrocery}
                onClearDoneGrocery={clearGroceryDone}
                onClearAllGrocery={clearGroceryAll}
                onAddGrocery={addGroceryItem}
              />
            </div>

            <div className="app-layout">
              <IngredientPicker
                selected={selected}
                filtered={filtered}
                selectedList={selectedList}
                category={category}
                search={search}
                onToggle={toggle}
                onRemove={remove}
                onClear={clear}
                onCategoryChange={setCategory}
                onSearchChange={setSearch}
                onFind={handleFind}
              nonStapleCount={nonStapleCount}
                loading={loading}
                pantry={pantry}
                onTogglePantry={togglePantry}
              />

              {/* Rate limit blocked banner */}
              {blocked && (
                <div style={st.rateBanner}>
                  <span style={st.rateBannerText}>⚠ {blocked.reason}</span>
                  <button style={st.rateBannerClose} onClick={clearBlock}>✕</button>
                </div>
              )}

              <ResultsPanel
                recipes={recipes}
                loading={loading}
                error={error}
                hasSearched={hasSearched}
                user={isAuthenticated ? user : null}
                savedIds={savedIds}
                onSave={saveRecipe}
                resultsRef={resultsRef}
                onSuggest={handleSuggest}
                  onAddToGrocery={addGroceryItem}
                onRemoveFromGrocery={removeGrocery}
              />
            </div>
          </main>

          <MobileFindBar
            count={selected.size}
            nonStapleCount={nonStapleCount}
            onClick={handleFind}
            loading={loading}
            resultsRef={resultsRef}
          />

          <footer style={st.footer} className="app-footer-mobile-pad">

            {/* Mobile coffee + meal planner row — hidden on desktop */}
            <div className="footer-mobile-actions">
              <Link to="/tips" style={st.tipsPill}
                onMouseEnter={e => e.currentTarget.style.opacity='0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity='1'}
              >📖 Cooking Tips</Link>
              <a
                href={process.env.REACT_APP_BMC_URL || 'https://buymeacoffee.com/ohapotato'}
                target="_blank" rel="noopener noreferrer"
                style={st.coffeePill}
                onMouseEnter={e => e.currentTarget.style.opacity='0.85'}
                onMouseLeave={e => e.currentTarget.style.opacity='1'}
              >☕ Buy me a coffee</a>
              {isAuthenticated && (
                <button style={st.mealPlanPill} onClick={handleOpenMealPlanner}
                  onMouseEnter={e => { e.currentTarget.style.background='var(--accent-orange)'; e.currentTarget.style.color='#fff'; }}
                  onMouseLeave={e => { e.currentTarget.style.background='transparent'; e.currentTarget.style.color='var(--text-secondary)'; }}
                >🗓️ Meal Planner</button>
              )}
            </div>

            {/* Nav links — clean pill row */}
            <div style={st.footerNav}>
              <Link to="/tips"    style={st.footerNavLink}>Cooking Tips</Link>
              <Link to="/about"   style={st.footerNavLink}>About</Link>
              <Link to="/contact" style={st.footerNavLink}>Contact</Link>
              <Link to="/terms"      style={st.footerNavLink}>Terms</Link>
              <Link to="/disclaimer" style={st.footerNavLink}>Disclaimer</Link>
              <Link to="/privacy"    style={st.footerNavLink}>Privacy</Link>
            </div>

            {/* Branding + search count */}
            <div style={st.footerBrand}>
              <span style={st.footerLogo}>Oh<span style={st.accent}>a</span>potato</span>
              <span style={st.footerDot}>·</span>
              <span style={st.footerLimit}>
                {remaining.daily} search{remaining.daily !== 1 ? 'es' : ''} left today
              </span>
            </div>

          </footer>

          {/* Profile panel */}
          {profileTab && isAuthenticated && user && (
            <ProfilePanel
              user={user}
              profile={profile}
              onUpdateProfile={updateProfile}
              saved={saved}
              savedIds={savedIds}
              onSave={saveRecipe}
              onRemove={removeRecipe}
              onClose={() => setProfileTab(null)}
              initialTab={profileTab}
              onLaunchMealPlanner={() => { setProfileTab(null); setMealPlannerOpen(true); }}
            />
          )}


          {mealPlannerOpen && isAuthenticated && (
            <MealPlanner
              profile={profile}
              savedRecipes={saved}
              pantry={pantry}
              onAddToGrocery={addGroceryItems}
              onClose={() => setMealPlannerOpen(false)}
            />
          )}
        </div>
      } />
    </Routes>
  );
}

const st = {
  toolbar: {
    display:'flex', justifyContent:'flex-start', alignItems:'center',
    gap:'8px', paddingBottom:'16px', flexWrap:'wrap',
  },
  premiumToast: {
    display:'flex', alignItems:'center', justifyContent:'space-between',
    gap:'10px', margin:'8px 0 0',
    background:'var(--accent-orange)', color:'#fff',
    borderRadius:'var(--radius-md)', padding:'10px 14px',
    fontSize:'13px', fontWeight:'600', fontFamily:'var(--font-body)',
    animation:'fadeUp 0.3s ease',
    boxShadow:'var(--shadow-xp)',
  },
  rateBanner: {
    display:'flex', alignItems:'center', justifyContent:'space-between',
    padding:'10px 14px', margin:'0 0 12px',
    background:'rgba(220,38,38,0.07)', border:'1px solid rgba(220,38,38,0.18)',
    borderRadius:'var(--radius-md)', gap:'10px',
  },
  rateBannerText: {
    fontSize:'13px', color:'#DC2626', fontFamily:'var(--font-body)',
    lineHeight:'1.45', flex:1,
  },
  rateBannerClose: {
    background:'none', border:'none', cursor:'pointer',
    fontSize:'14px', color:'#DC2626', padding:'2px', flexShrink:0,
    fontFamily:'inherit',
  },
  footer: {
    borderTop:'1px solid var(--border-base)',
    padding:'20px 24px 16px',
    textAlign:'center',
    display:'flex', flexDirection:'column', alignItems:'center', gap:'12px',
    transition:'border-color 0.25s ease',
  },
  // Nav links row
  footerNav: {
    display:'flex', alignItems:'center', gap:'6px',
    flexWrap:'wrap', justifyContent:'center',
  },
  footerNavLink: {
    fontSize:'13px', color:'var(--text-muted)', textDecoration:'none',
    fontFamily:'var(--font-body)', fontWeight:'500',
    padding:'5px 12px', borderRadius:'var(--radius-pill)',
    border:'1px solid var(--border-base)',
    transition:'all 0.14s ease',
    opacity:0.8,
  },
  // Branding row
  footerBrand: {
    display:'flex', alignItems:'center', gap:'8px',
    fontFamily:'var(--font-body)',
  },
  footerLogo: {
    fontSize:'13px', fontWeight:'600', color:'var(--text-muted)', opacity:0.7,
    fontFamily:'var(--font-display)',
  },
  footerDot: { fontSize:'10px', color:'var(--text-muted)', opacity:0.3 },
  footerLimit: { fontSize:'12px', color:'var(--text-muted)', opacity:0.5 },
  accent: { color:'var(--accent-orange)' },
  // Coffee pill — shown on mobile via CSS, hidden on desktop
  tipsPill: {
    display:'inline-flex', alignItems:'center', gap:'6px',
    padding:'8px 18px', borderRadius:'var(--radius-pill)',
    background:'var(--accent-orange-bg)',
    border:'1px solid var(--accent-orange)',
    color:'var(--accent-orange)',
    fontFamily:'var(--font-body)', fontSize:'13px', fontWeight:'600',
    textDecoration:'none', transition:'opacity 0.15s ease',
  },
  coffeePill: {
    display:'inline-flex', alignItems:'center', gap:'6px',
    padding:'8px 18px', borderRadius:'var(--radius-pill)',
    background:'#FFDD00', color:'#1a1a1a',
    fontFamily:'var(--font-body)', fontSize:'13px', fontWeight:'600',
    textDecoration:'none', transition:'opacity 0.15s ease',
    border:'none', cursor:'pointer',
  },
  mealPlanPill: {
    display:'inline-flex', alignItems:'center', gap:'6px',
    padding:'8px 18px', borderRadius:'var(--radius-pill)',
    background:'transparent', color:'var(--text-secondary)',
    fontFamily:'var(--font-body)', fontSize:'13px', fontWeight:'500',
    border:'1px solid var(--border-base)',
    cursor:'pointer', transition:'all 0.14s ease',
  },
};
